/* qeslint-disable */

import { Do } from "./do";
import { setBounds } from "../utils";

const SNAP_FULL_SCREEN = "full-screen";
const SNAP_TOP_EDGE = "snap-top-edge"; // or actually top half
const SNAP_LEFT_EDGE = "snap-left-edge";
const SNAP_RIGHT_EDGE = "snap-right-edge";
const SNAP_BOTTOM_EDGE = "snap-bottom-edge";
const SNAP_DOCK_BOTTOM = "dock-bottom";

type SnapType =
    | typeof SNAP_FULL_SCREEN
    | typeof SNAP_TOP_EDGE
    | typeof SNAP_LEFT_EDGE
    | typeof SNAP_RIGHT_EDGE
    | typeof SNAP_BOTTOM_EDGE
    | typeof SNAP_DOCK_BOTTOM
    | null;

interface PointerStart {
    x: number;
    y: number;
    cx: number;
    cy: number;
    w: number;
    h: number;
    isResizing: boolean;
    isMoving: boolean;
    onTopEdge: boolean;
    onLeftEdge: boolean;
    onRightEdge: boolean;
    onBottomEdge: boolean;
}

interface PreSnapped {
    width: number;
    height: number;
    top: number;
    left: number;
}

interface SnapBounds {
    left: number;
    top: number;
    width: number;
    height: number;
}

/*
The Docking Widget

1. when .allowMove(true) is set, the pane becomes draggable
2. when dragging, if the pointer to near to the edges,
   it resizes the ghost pannel as a suggestion to snap into the
   suggested position
3. user can either move pointer away or let go of the cursor,
   allow the pane to be resized and snapped into position

My origin implementation from https://codepen.io/zz85/pen/gbOoVP

args eg.
	let pane = document.getElementById('pane');
	let ghostpane = document.getElementById('ghostpane');
	widget = new DockingWindow(pane, ghostpane)

	title_dom.addEventListener('mouseover', function() {
		widget.allowMove(true);
	});

	title_dom.addEventListener('mouseout', function() {
		widget.allowMove(false);
	});

	resize_full.onClick(() => {
		widget.maximize() // fill to screen
	})

	// TODO callback when pane is resized
	widget.resizes.do(() => {
		something
	})
*/

export class DockingWindow {
    private panel: HTMLElement;
    private ghostPanel: HTMLElement;
    public resizes: Do;

    // Minimum resizable area
    private readonly minWidth = 100;
    private readonly minHeight = 80;

    // Thresholds
    private readonly FULLSCREEN_MARGINS = 2;
    private readonly SNAP_MARGINS = 8;
    private readonly MARGINS = 2;

    // End of what's configurable.
    private pointerStart: PointerStart | null = null;
    private onRightEdge: boolean = false;
    private onBottomEdge: boolean = false;
    private onLeftEdge: boolean = false;
    private onTopEdge: boolean = false;

    private preSnapped: PreSnapped | null = null;

    private bounds: DOMRect | null = null;
    private x: number = 0;
    private y: number = 0;

    private redraw: boolean = false;

    private allowDragging: boolean = true;
    private snapType: SnapType = null;

    private snapBounds: SnapBounds = { left: 0, top: 0, width: 0, height: 0 };
    private e: MouseEvent | Touch | null = null; // current mousemove event

    constructor(panel: HTMLElement, ghostPanel: HTMLElement) {
        this.panel = panel;
        this.ghostPanel = ghostPanel;
        this.resizes = new Do();

        this.init();
    }

    allowMove(allow: boolean): void {
        this.allowDragging = allow;
    }

    private canMove(): boolean {
        return this.allowDragging;
    }

    maximize(): void {
        if (!this.preSnapped && this.bounds) {
            this.preSnapped = {
                width: this.bounds.width,
                height: this.bounds.height,
                top: this.bounds.top,
                left: this.bounds.left
            };

            this.snapType = SNAP_FULL_SCREEN;
            this.resizeEdges();
        } else if (this.bounds) {
            setBounds(this.panel, this.bounds.left, this.bounds.top, this.bounds.width, this.bounds.height);
            this.calculateBounds();
            this.snapType = null;
            this.preSnapped = null;
        }
    }

    /* DOM Utils */
    private hideGhostPane(): void {
        // hide the hinter, animatating to the pane's bounds
        if (this.bounds) {
            setBounds(this.ghostPanel, this.bounds.left, this.bounds.top, this.bounds.width, this.bounds.height);
        }

        this.ghostPanel.style.opacity = "0";
    }

    private onTouchDown = (e: TouchEvent): void => {
        if (e.touches[0]) {
            this.onDoMouseDown(e.touches[0] as any);
            e.preventDefault();
        }
    };

    private onTouchMove = (e: TouchEvent): void => {
        if (e.touches[0]) {
            this.onMouseMove(e.touches[0] as any);
        }
    };

    private onTouchEnd = (e: TouchEvent): void => {
        if (e.changedTouches[0]) {
            this.onUp(e.changedTouches[0] as any);
        }
    };

    private onMouseDown = (e: MouseEvent): void => {
        this.onDoMouseDown(e);
    };

    private onMouseUp = (e: MouseEvent): void => {
        this.onUp(e);
    };

    private onDoMouseDown(e: MouseEvent): void {
        this.calculateBounds(e);

        const isResizing = this.onRightEdge || this.onBottomEdge || this.onTopEdge || this.onLeftEdge;
        const isMoving = !isResizing && this.canMove();

        this.pointerStart = {
            x: this.x,
            y: this.y,
            cx: e.clientX,
            cy: e.clientY,
            w: this.bounds!.width,
            h: this.bounds!.height,
            isResizing: isResizing,
            isMoving: isMoving,
            onTopEdge: this.onTopEdge,
            onLeftEdge: this.onLeftEdge,
            onRightEdge: this.onRightEdge,
            onBottomEdge: this.onBottomEdge
        };

        if (isResizing || isMoving) {
            e.preventDefault();

            if ("stopPropagation" in e) {
                e.stopPropagation();
            }
        }
    }

    private calculateBounds(e?: MouseEvent | Touch): void {
        this.bounds = this.panel.getBoundingClientRect();

        if (e) {
            this.x = e.clientX - this.bounds.left;
            this.y = e.clientY - this.bounds.top;
        }

        this.onTopEdge = this.y < this.MARGINS;
        this.onLeftEdge = this.x < this.MARGINS;
        this.onRightEdge = this.x >= this.bounds.width - this.MARGINS;
        this.onBottomEdge = this.y >= this.bounds.height - this.MARGINS;
    }

    private onMouseMove = (ee: MouseEvent | Touch): void => {
        this.e = ee;
        this.calculateBounds(ee);

        this.redraw = true;
    };

    private animate = (): void => {
        requestAnimationFrame(this.animate);

        if (!this.redraw) {
            return;
        }

        this.redraw = false;

        this.updateCursor();

        if (!this.pointerStart || !this.e || !this.bounds) {
            return;
        }

        if (this.pointerStart.isResizing) {
            this.handleResize();

            return;
        }

        if (this.pointerStart.isMoving) {
            this.handleDrag();
        }
    };

    private updateCursor(): void {
        if ((this.onRightEdge && this.onBottomEdge) || (this.onLeftEdge && this.onTopEdge)) {
            this.panel.style.cursor = "nwse-resize";
        } else if ((this.onRightEdge && this.onTopEdge) || (this.onBottomEdge && this.onLeftEdge)) {
            this.panel.style.cursor = "nesw-resize";
        } else if (this.onRightEdge || this.onLeftEdge) {
            this.panel.style.cursor = "ew-resize";
        } else if (this.onBottomEdge || this.onTopEdge) {
            this.panel.style.cursor = "ns-resize";
        } else if (this.canMove()) {
            this.panel.style.cursor = "move";
        } else {
            this.panel.style.cursor = "default";
        }
    }

    private handleResize(): void {
        if (!this.pointerStart || !this.e) {
            return;
        }

        if (this.pointerStart.onRightEdge) {
            this.panel.style.width = `${Math.max(this.x, this.minWidth)}px`;
        }

        if (this.pointerStart.onBottomEdge) {
            this.panel.style.height = `${Math.max(this.y, this.minHeight)}px`;
        }

        if (this.pointerStart.onLeftEdge) {
            const currentWidth = Math.max(this.pointerStart.cx - this.e.clientX + this.pointerStart.w, this.minWidth);

            if (currentWidth > this.minWidth) {
                this.panel.style.width = `${currentWidth}px`;
                this.panel.style.left = `${this.e.clientX}px`;
            }
        }

        if (this.pointerStart.onTopEdge) {
            const currentHeight = Math.max(this.pointerStart.cy - this.e.clientY + this.pointerStart.h, this.minHeight);

            if (currentHeight > this.minHeight) {
                this.panel.style.height = `${currentHeight}px`;
                this.panel.style.top = `${this.e.clientY}px`;
            }
        }

        this.hideGhostPane();

        if (this.bounds) {
            this.resizes.fire(this.bounds.width, this.bounds.height);
        }
    }

    private handleDrag(): void {
        if (!this.pointerStart || !this.e) {
            return;
        }

        const snapType = this.checkSnapType();

        if (snapType) {
            this.calcSnapBounds(snapType);

            const { left, top, width, height } = this.snapBounds;

            setBounds(this.ghostPanel, left, top, width, height);
            this.ghostPanel.style.opacity = "0.2";
        } else {
            this.hideGhostPane();
        }

        if (this.preSnapped) {
            setBounds(
                this.panel,
                this.e.clientX - this.preSnapped.width / 2,
                this.e.clientY - Math.min(this.pointerStart.y, this.preSnapped.height),
                this.preSnapped.width,
                this.preSnapped.height
            );

            return;
        }

        // moving
        this.panel.style.top = `${this.e.clientY - this.pointerStart.y}px`;
        this.panel.style.left = `${this.e.clientX - this.pointerStart.x}px`;
    }

    private checkSnapType(): SnapType {
        if (!this.e) {
            return null;
        }

        // drag to full screen
        if (this.e.clientY < this.FULLSCREEN_MARGINS) {
            return SNAP_FULL_SCREEN;
        }

        // drag for top half screen
        if (this.e.clientY < this.SNAP_MARGINS) {
            return SNAP_TOP_EDGE;
        }

        // drag for left half screen
        if (this.e.clientX < this.SNAP_MARGINS) {
            return SNAP_LEFT_EDGE;
        }

        // drag for right half screen
        if (window.innerWidth - this.e.clientX < this.SNAP_MARGINS) {
            return SNAP_RIGHT_EDGE;
        }

        // drag for bottom half screen
        if (window.innerHeight - this.e.clientY < this.SNAP_MARGINS) {
            return SNAP_BOTTOM_EDGE;
        }

        return null;
    }

    private calcSnapBounds(snapType: SnapType): void {
        if (!snapType || !this.bounds) {
            return;
        }

        let width: number;
        let height: number;
        let left: number;
        let top: number;

        switch (snapType) {
            case SNAP_FULL_SCREEN:
                width = window.innerWidth;
                height = window.innerHeight;
                left = 0;
                top = 0;
                break;
            case SNAP_TOP_EDGE:
                width = window.innerWidth;
                height = window.innerHeight / 2;
                left = 0;
                top = 0;
                break;
            case SNAP_LEFT_EDGE:
                width = window.innerWidth / 2;
                height = window.innerHeight;
                left = 0;
                top = 0;
                break;
            case SNAP_RIGHT_EDGE:
                width = window.innerWidth / 2;
                height = window.innerHeight;
                left = window.innerWidth - width;
                top = 0;
                break;
            case SNAP_BOTTOM_EDGE:
                width = window.innerWidth;
                height = window.innerHeight / 3;
                left = 0;
                top = window.innerHeight - height;
                break;
            case SNAP_DOCK_BOTTOM:
                width = this.bounds.width;
                height = this.bounds.height;
                left = (window.innerWidth - width) * 0.5;
                top = window.innerHeight - height;
                break;
            default:
                // This should never happen due to early return, but required by eslint
                if (this.bounds) {
                    width = this.bounds.width;
                    height = this.bounds.height;
                    left = this.bounds.left;
                    top = this.bounds.top;
                } else {
                    width = 0;
                    height = 0;
                    left = 0;
                    top = 0;
                }

                break;
        }

        Object.assign(this.snapBounds, { left, top, width, height });
    }

    /* When one of the edges is move, resize panel */
    private resizeEdges(): void {
        if (!this.snapType) {
            return;
        }

        this.calcSnapBounds(this.snapType);

        const { left, top, width, height } = this.snapBounds;

        setBounds(this.panel, left, top, width, height);
        setBounds(this.ghostPanel, left, top, width, height);

        this.resizes.fire(width, height);
    }

    private onUp(e: MouseEvent | Touch): void {
        this.calculateBounds(e);

        if (this.pointerStart && this.pointerStart.isMoving) {
            // Snap
            this.snapType = this.checkSnapType();

            if (this.snapType && this.bounds) {
                this.preSnapped = {
                    width: this.bounds.width,
                    height: this.bounds.height,
                    top: this.bounds.top,
                    left: this.bounds.left
                };

                this.resizeEdges();
            } else {
                this.preSnapped = null;
            }

            this.hideGhostPane();
        }

        this.pointerStart = null;
    }

    private init(): void {
        window.addEventListener("resize", () => {
            this.resizeEdges();
        });

        // Mouse events
        this.panel.addEventListener("mousedown", this.onMouseDown);
        document.addEventListener("mousemove", this.onMouseMove);
        document.addEventListener("mouseup", this.onMouseUp);

        // Touch events
        this.panel.addEventListener("touchstart", this.onTouchDown);
        document.addEventListener("touchmove", this.onTouchMove);
        document.addEventListener("touchend", this.onTouchEnd);

        this.bounds = this.panel.getBoundingClientRect();
        this.snapType = SNAP_DOCK_BOTTOM;

        // use setTimeout as a hack to get dimensions correctly! :(
        setTimeout(() => {
            this.resizeEdges();

            // ??
            // panel.style.display = 'none';
            this.ghostPanel.style.display = "none";
        });

        this.hideGhostPane();
        this.animate();
    }
}
